import { createRouter, createWebHistory } from 'vue-router'
import {
  requireAuth,
  requireReceiptAccess,
  requireAuthWithInvitation,
  requireSuperAdmin,
  requireProTeam,
  requireRefundData,
  requireGuest,
  requireMaintenanceMode,
} from './authGuard'

import DashboardView from '@/views/DashboardView.vue'
import AccountView from '@/views/AccountView.vue'
import AdminView from '@/views/admin/AdminView.vue'
import AdminSalespersonView from '@/views/admin/AdminSalespersonView.vue'
import AdminSettingsView from '@/views/admin/AdminSettingsView.vue'
import AdminBillingsView from '@/views/admin/AdminBillingsView.vue'
import AdminAppleTransactionsView from '@/views/admin/AdminAppleTransactionsView.vue'
import AdminCouponsView from '@/views/admin/AdminCouponsView.vue'
import AdminUsersView from '@/views/admin/AdminUsersView.vue'
import AdminUserHistoryView from '@/views/admin/AdminUserHistoryView.vue'
import AdminTemplatesView from '@/views/admin/AdminTemplatesView.vue'
import AdminGlobalSettingsView from '@/views/admin/AdminGlobalSettingsView.vue'
import AdminTeamsView from '@/views/admin/AdminTeamsView.vue'
import AdminStoresView from '@/views/admin/AdminStoresView.vue'

import ProfileView from '@/views/ProfileView.vue'
import NotificationsView from '@/views/NotificationsView.vue'
import NotificationSettingsView from '@/views/NotificationSettingsView.vue'
import UnsubscribeView from '@/views/UnsubscribeView.vue'
import AdminPricingView from '@/views/admin/AdminPricingView.vue'

import LoginView from '@/views/auth/LoginView.vue'
import RegisterView from '@/views/auth/RegisterView.vue'
import ForgotPasswordView from '@/views/auth/ForgotPasswordView.vue'
import ResetPasswordView from '@/views/auth/ResetPasswordView.vue'
import VerifyEmailView from '@/views/auth/VerifyEmailView.vue'
import ChangeEmailView from '@/views/auth/ChangeEmailView.vue'
import LoginSuccessView from '@/views/auth/LoginSuccessView.vue'
import ReceiptAccessView from '@/views/auth/ReceiptAccessView.vue'
import MaintenanceModeView from '@/views/MaintenanceModeView.vue'

import TeamsListView from '@/views/teams/TeamsListView.vue'
import TeamsCreateView from '@/views/teams/TeamsCreateView.vue'
import TeamsDetailView from '@/views/teams/TeamsDetailView.vue'
import TeamsJoinView from '@/views/teams/TeamsJoinView.vue'
import TeamsRequestsView from '@/views/teams/TeamsRequestsView.vue'
import TeamsInvitationCodesView from '@/views/teams/TeamsInvitationCodesView.vue'
import TeamsTransfersView from '@/views/teams/TeamsTransfersView.vue'
import TeamsConsumptionView from '@/views/teams/TeamsConsumptionView.vue'
import TeamsMembersView from '@/views/teams/TeamsMembersView.vue'

import TeamsCustomizationTeamInfoView from '@/views/teams/TeamsCustomizationTeamInfoView.vue'
import TeamsCustomizationLogoView from '@/views/teams/TeamsCustomizationLogoView.vue'
import TeamsCustomizationRefundView from '@/views/teams/TeamsCustomizationRefundView.vue'
import TeamsCustomizationTemplatesView from '@/views/teams/TeamsCustomizationTemplatesView.vue'
import TeamsTemplateDraftsView from '@/views/teams/TeamsTemplateDraftsView.vue'
import TeamsRefundView from '@/views/teams/TeamsRefundView.vue'
import TeamsFieldsView from '@/views/teams/TeamsFieldsView.vue'
import TeamsReceiptsAdminView from '@/views/teams/TeamsReceiptsAdminView.vue'
import TeamsUpgradeView from '@/views/teams/TeamsUpgradeView.vue'
import TeamsEditView from '@/views/teams/TeamsEditView.vue'
import TeamsSchemaEditView from '@/views/teams/TeamsSchemaEditView.vue'

import ReceiptsView from '@/views/receipt/ReceiptsView.vue'
import ReceiptWizard from '@/views/receipt/ReceiptWizard/ReceiptWizard.vue'
import ReceiptDetailView from '@/views/receipt/ReceiptDetailView.vue'


import PayPalReturnView from '@/views/PayPalReturnView.vue'
import GettingStartedView from '@/views/GettingStartedView.vue'
import SalesView from '@/views/SalesView.vue'
import CoinPurchaseView from '@/views/CoinPurchaseView.vue'

const routes = [
  {
    path: '/',
    redirect: '/auth/login',
    beforeEnter: requireGuest,
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: DashboardView,
    beforeEnter: requireAuth,
  },
  {
    path: '/account',
    name: 'account',
    component: AccountView,
    beforeEnter: requireAuth,
  },
  {
    path: '/admin',
    name: 'admin',
    component: AdminView,
    beforeEnter: requireSuperAdmin,
  },
  {
    path: '/admin/salesperson',
    name: 'admin-salesperson',
    component: AdminSalespersonView,
    beforeEnter: requireSuperAdmin,
  },
  {
    path: '/admin/settings',
    name: 'admin-settings',
    component: AdminSettingsView,
    beforeEnter: requireSuperAdmin,
  },
  {
    path: '/admin/billings',
    name: 'admin-billings',
    component: AdminBillingsView,
    beforeEnter: requireSuperAdmin,
  },
  {
    path: '/admin/apple-transactions',
    name: 'admin-apple-transactions',
    component: AdminAppleTransactionsView,
    beforeEnter: requireSuperAdmin,
  },
  {
    path: '/admin/pricing',
    name: 'admin-pricing',
    component: AdminPricingView,
    beforeEnter: requireSuperAdmin,
  },
  {
    path: '/admin/coupons',
    name: 'admin-coupons',
    component: AdminCouponsView,
    beforeEnter: requireSuperAdmin,
  },
  {
    path: '/admin/users',
    name: 'admin-users',
    component: AdminUsersView,
    beforeEnter: requireSuperAdmin,
  },
  {
    path: '/admin/users/:id/history',
    name: 'admin-user-history',
    component: AdminUserHistoryView,
    beforeEnter: requireSuperAdmin,
  },
  {
    path: '/admin/global-settings',
    name: 'admin-global-settings',
    component: AdminGlobalSettingsView,
    beforeEnter: requireSuperAdmin,
  },
  {
    path: '/admin/templates',
    name: 'admin-templates',
    component: AdminTemplatesView,
    beforeEnter: requireSuperAdmin,
  },
  {
    path: '/admin/teams',
    name: 'admin-teams',
    component: AdminTeamsView,
    beforeEnter: requireSuperAdmin,
  },
  {
    path: '/admin/stores',
    name: 'admin-stores',
    component: AdminStoresView,
    beforeEnter: requireSuperAdmin,
  },
  {
    path: '/profile',
    name: 'profile',
    component: ProfileView,
    beforeEnter: requireAuth,
  },
  {
    path: '/notifications',
    name: 'notifications',
    component: NotificationsView,
    beforeEnter: requireAuth,
  },
  {
    path: '/notification-settings',
    name: 'notification-settings',
    component: NotificationSettingsView,
    beforeEnter: requireAuth,
  },
  {
    path: '/unsubscribe/:token',
    name: 'unsubscribe',
    component: UnsubscribeView,
  },
  {
    path: '/maintenance-mode',
    name: 'maintenance-mode',
    component: MaintenanceModeView,
    beforeEnter: requireMaintenanceMode,
  },

  {
    path: '/auth/login',
    name: 'login',
    component: LoginView,
  },
  {
    path: '/auth/register',
    name: 'register',
    component: RegisterView,
  },
  {
    path: '/auth/forgot-password',
    name: 'forgot-password',
    component: ForgotPasswordView,
  },
  {
    path: '/auth/reset-password',
    name: 'reset-password',
    component: ResetPasswordView,
  },
  {
    path: '/auth/verify',
    name: 'verify-email',
    component: VerifyEmailView,
  },
  {
    path: '/auth/change-email',
    name: 'change-email',
    component: ChangeEmailView,
    beforeEnter: requireAuth,
  },
  {
    path: '/auth/login-success',
    name: 'login-success',
    component: LoginSuccessView,
  },
  {
    path: '/receipts/:id/access',
    name: 'receipt-access',
    component: ReceiptAccessView,
    props: true,
  },

  {
    path: '/login',
    redirect: '/auth/login',
  },
  {
    path: '/register',
    redirect: '/auth/register',
  },
  {
    path: '/forgot-password',
    redirect: '/auth/forgot-password',
  },
  {
    path: '/reset-password',
    redirect: '/auth/reset-password',
  },
  {
    path: '/verify-email',
    redirect: '/auth/verify-email',
  },
  {
    path: '/change-email',
    redirect: '/auth/change-email',
  },
  {
    path: '/login-success',
    redirect: '/auth/login-success',
  },

  {
    path: '/teams',
    name: 'teams-list',
    component: TeamsListView,
    beforeEnter: requireAuth,
  },
  {
    path: '/teams/create',
    name: 'teams-create',
    component: TeamsCreateView,
    beforeEnter: requireAuth,
  },
  {
    path: '/teams/:id',
    name: 'teams-detail',
    component: TeamsReceiptsAdminView,
    beforeEnter: requireAuth,
  },
  {
    path: '/teams/:id/settings',
    name: 'teams-settings',
    component: TeamsDetailView,
    beforeEnter: requireAuth,
  },
  {
    path: '/teams/join',
    name: 'teams-join',
    component: TeamsJoinView,
    beforeEnter: requireAuth,
  },
  {
    path: '/join/:code',
    name: 'join-with-code',
    component: TeamsJoinView,
    beforeEnter: requireAuthWithInvitation,
    props: true,
  },
  {
    path: '/teams/requests',
    name: 'teams-requests',
    component: TeamsRequestsView,
    beforeEnter: requireAuth,
  },
  {
    path: '/teams/:id/invitations',
    name: 'teams-invitation-codes',
    component: TeamsInvitationCodesView,
    beforeEnter: [requireAuth, requireRefundData],
  },
  {
    path: '/teams/:id/transfers',
    name: 'teams-transfers',
    component: TeamsTransfersView,
    beforeEnter: [requireAuth, requireRefundData],
  },
  {
    path: '/teams/:id/consumption',
    name: 'teams-consumption',
    component: TeamsConsumptionView,
    beforeEnter: [requireAuth, requireRefundData],
  },
  {
    path: '/teams/:id/members',
    name: 'teams-members',
    component: TeamsMembersView,
    beforeEnter: [requireAuth, requireRefundData],
  },
  {
    path: '/teams/:id/customization/team-info',
    name: 'teams-customization-team-info',
    component: TeamsCustomizationTeamInfoView,
    beforeEnter: requireProTeam,
  },
  {
    path: '/teams/:id/customization/logo',
    name: 'teams-customization-logo',
    component: TeamsCustomizationLogoView,
    beforeEnter: requireProTeam,
  },
  {
    path: '/teams/:id/customization/refund',
    name: 'teams-customization-refund',
    component: TeamsCustomizationRefundView,
    beforeEnter: requireProTeam,
  },
  {
    path: '/teams/:id/customization/templates',
    name: 'teams-customization-templates',
    component: TeamsCustomizationTemplatesView,
    beforeEnter: requireProTeam,
  },
  {
    path: '/teams/:id/customization/templates/drafts',
    name: 'teams-template-drafts',
    component: TeamsTemplateDraftsView,
    beforeEnter: requireProTeam,
  },
  {
    path: '/teams/:id/refund',
    name: 'teams-refund',
    component: TeamsRefundView,
    beforeEnter: requireAuth,
  },
  {
    path: '/teams/:id/fields',
    name: 'teams-fields',
    component: TeamsFieldsView,
    beforeEnter: [requireAuth, requireRefundData],
  },
  {
    path: '/teams/:id/receipts-admin',
    name: 'teams-receipts-admin',
    component: TeamsReceiptsAdminView,
    beforeEnter: requireAuth,
  },
  {
    path: '/teams/:id/upgrade',
    name: 'teams-upgrade',
    component: TeamsUpgradeView,
    beforeEnter: requireAuth,
  },
  {
    path: '/teams/:id/edit',
    name: 'teams-edit',
    component: TeamsEditView,
    beforeEnter: requireAuth,
  },
  {
    path: '/teams/:id/schema-edit',
    name: 'teams-schema-edit',
    component: TeamsSchemaEditView,
    beforeEnter: requireAuth,
  },

  {
    path: '/receipts',
    name: 'receipts',
    component: ReceiptsView,
    beforeEnter: requireAuth,
  },
  {
    path: '/receipts/create',
    name: 'receipt-create',
    component: ReceiptWizard,
    beforeEnter: requireAuth,
  },
  {
    path: '/receipts/:id',
    name: 'receipt-detail',
    beforeEnter: requireReceiptAccess,
    component: ReceiptDetailView,
  },
  {
    path: '/receipt/:id',
    name: 'receipt-detail-two',
    beforeEnter: requireReceiptAccess,
    component: ReceiptDetailView,
  },

  {
    path: '/buy-tokens',
    redirect: '/account',
  },

  {
    path: '/paypal-return',
    name: 'paypal-return',
    component: PayPalReturnView,
    beforeEnter: requireAuth,
  },
  {
    path: '/getting-started',
    name: 'getting-started',
    component: GettingStartedView,
    beforeEnter: requireAuth,
  },
  {
    path: '/sales',
    name: 'sales',
    component: SalesView,
    beforeEnter: requireAuth,
  },
  {
    path: '/purchase',
    name: 'purchase',
    component: CoinPurchaseView,
    beforeEnter: requireAuth,
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/dashboard',
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

export default router
